/**
 * CookieBanner - GDPR Cookie Consent Banner
 * Shows a friendly cookie consent message at the bottom of the screen
 */

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const TEXTS = {
  it: {
    message: 'Ciao Maestra! Usiamo qualche biscottino (cookie) tecnico per far funzionare la magia di Sabrina. Niente tracciamento pubblicitario, promesso!',
    policy: 'Leggi Policy',
    accept: 'Accetto i Biscotti',
    close: 'Chiudi',
  },
  es: {
    message: 'Hola Profe! Usamos algunas galletas (cookies) tecnicas para que la magia de Sabrina funcione. Sin rastreo publicitario, prometido!',
    policy: 'Leer Politica',
    accept: 'Acepto las Galletas',
    close: 'Cerrar',
  },
};

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const location = useLocation();
  const isSpanish = location.pathname.startsWith('/es');
  const t = isSpanish ? TEXTS.es : TEXTS.it;

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'true');
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-amber-100 rounded-lg shrink-0">
                  <Cookie className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-gray-700 text-sm sm:text-base">
                  {t.message}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                <Link to="/privacy" className="flex-1 sm:flex-none">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto text-gray-600 hover:text-gray-800"
                    data-testid="cookie-policy-btn"
                  >
                    {t.policy}
                  </Button>
                </Link>
                <Button
                  onClick={handleAccept}
                  size="sm"
                  className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="cookie-accept-btn"
                >
                  {t.accept}
                </Button>
              </div>

              <button
                onClick={handleAccept}
                className="absolute top-2 right-2 sm:hidden p-1 text-gray-400 hover:text-gray-600"
                aria-label={t.close}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
